/* jmw-review-filter.js — per-page review exclusion for "JudgeMe - Reviews Widget".

   WHY THIS EXISTS
   Judge.me serves reviews from its own API, 5-10 at a time, and owns the pagination.
   Hiding unwanted reviews in CSS/DOM therefore produces ragged pages (5 -> 3 -> 5 -> 4),
   which is the exact thing we were asked to avoid. Liquid cannot filter them either:
   it has no HTTP, and the Judge.me REST API needs a secret token that must never reach
   the browser. So the only correct fix is to take over list rendering AND pagination.

   HOW
   Fetch every review for the product from the same public endpoint Judge.me's own JS
   uses, filter locally, then paginate ourselves. Judge.me's per-review setup is
   re-applied through jdgm.customizeReviews() so thumbs / lightbox / timestamps survive.

   Measured facts this relies on (purblack.com, Aug 2026):
     GET https://api.judge.me/reviews/reviews_for_widget -> { html, total_count, page }
     public, no auth, CORS-permitted; per_page is capped server-side at ~30.

   FAILURE POLICY
   Any error at any stage reveals the untouched native widget instead of a blank
   section. The filter is a display-only enhancement, never a hard dependency.

   DELIBERATELY NOT TOUCHED
   JSON-LD / microdata. Structured data must keep reporting all real reviews; rewriting
   the aggregate rating to match a page-specific subset would misreport it to Google. */

(function () {
  'use strict';

  var API = 'https://api.judge.me/reviews/reviews_for_widget';
  var FETCH_PER_PAGE = 30;   // asking for more returns the same; the cap is server-side
  var MAX_REQUESTS = 40;     // runaway guard, not a real limit (40 x 30 = 1200 reviews)
  var CONCURRENCY = 4;
  var CACHE_VERSION = 'v1';
  var READY_CLASS = 'jmwrf-ready';
  var MARK = 'jmwrf';        // data-jmwrf marks nodes we injected

  /* ---------------------------------------------------------------- utils */

  // Lowercase, strip diacritics, straighten curly apostrophes, collapse whitespace.
  // Used for the debug excerpt and for reading the sort dropdown label.
  function fold(s) {
    return String(s || '')
      .normalize('NFD').replace(/\p{Diacritic}/gu, '')
      .replace(/[‘’ʼ]/g, "'")
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim();
  }

  function parseIds(raw) {
    var set = Object.create(null);
    String(raw || '').split(/[,\s]+/).forEach(function (v) {
      v = v.trim();
      if (v) set[v.toLowerCase()] = 1;
    });
    return set;
  }

  function shopDomain() {
    try {
      if (window.jdgm && typeof window.jdgm.shopDomain === 'function') {
        var d = window.jdgm.shopDomain();
        if (d) return d;
      }
    } catch (e) {}
    if (window.jdgmSettings && window.jdgmSettings.shop_domain) return window.jdgmSettings.shop_domain;
    if (window.Shopify && window.Shopify.shop) return window.Shopify.shop;
    return null;
  }

  function waitFor(test, timeout) {
    return new Promise(function (resolve) {
      var v = test();
      if (v) { resolve(v); return; }
      var t0 = Date.now();
      var iv = setInterval(function () {
        var got = test();
        if (got || Date.now() - t0 > timeout) { clearInterval(iv); resolve(got || null); }
      }, 150);
    });
  }

  // Bounded worker pool. A rejected item resolves to null rather than killing the run:
  // one missing page of reviews is far better than falling back to unfiltered output.
  function pool(items, limit, worker) {
    return new Promise(function (resolve) {
      if (!items.length) { resolve([]); return; }
      var out = new Array(items.length), next = 0, active = 0, done = 0;
      function step() {
        while (active < limit && next < items.length) {
          (function (idx) {
            active++; next++;
            worker(items[idx]).then(function (v) { out[idx] = v; }, function () { out[idx] = null; })
              .then(function () {
                active--; done++;
                if (done === items.length) resolve(out); else step();
              });
          })(next);
        }
      }
      step();
    });
  }

  /* ----------------------------------------------------------- fetch layer */

  function fetchPage(shop, productId, page) {
    var q = new URLSearchParams({
      url: shop, shop_domain: shop, platform: 'shopify',
      product_id: String(productId), page: String(page), per_page: String(FETCH_PER_PAGE)
    });
    return fetch(API + '?' + q.toString(), { credentials: 'omit' }).then(function (r) {
      if (!r.ok) throw new Error('judge.me HTTP ' + r.status);
      return r.json();
    });
  }

  function nodesFrom(html) {
    var doc = new DOMParser().parseFromString(html || '', 'text/html');
    return Array.prototype.slice.call(doc.querySelectorAll('.jdgm-rev'));
  }

  function fetchAll(shop, productId, log) {
    return fetchPage(shop, productId, 1).then(function (first) {
      var total = parseInt(first.total_count, 10) || 0;
      var firstNodes = nodesFrom(first.html);
      var wanted = total ? Math.ceil(total / FETCH_PER_PAGE) : 1;
      var pages = Math.min(wanted, MAX_REQUESTS);
      if (pages < wanted) {
        log('capped at ' + MAX_REQUESTS + ' requests; ' + (wanted - pages) + ' page(s) of reviews not fetched');
      }
      if (pages <= 1) return { total: total, nodes: firstNodes };

      var rest = [];
      for (var p = 2; p <= pages; p++) rest.push(p);
      return pool(rest, CONCURRENCY, function (n) {
        return fetchPage(shop, productId, n).then(function (j) { return nodesFrom(j.html); });
      }).then(function (chunks) {
        var all = firstNodes.slice();
        chunks.forEach(function (c) { if (c) all = all.concat(c); });
        return { total: total, nodes: all };
      });
    });
  }

  /* ---------------------------------------------------------------- cache */

  function ckey(productId) { return 'jmwrf:' + productId + ':' + CACHE_VERSION; }

  function readCache(productId) {
    try {
      var o = JSON.parse(sessionStorage.getItem(ckey(productId)) || 'null');
      if (!o || !Array.isArray(o.html) || !o.html.length) return null;
      var host = document.createElement('div');
      host.innerHTML = o.html.join('');
      var nodes = Array.prototype.slice.call(host.querySelectorAll('.jdgm-rev'));
      return nodes.length ? { total: o.total, nodes: nodes } : null;
    } catch (e) { return null; }
  }

  function writeCache(productId, total, nodes) {
    try {
      sessionStorage.setItem(ckey(productId), JSON.stringify({
        total: total,
        html: nodes.map(function (n) { return n.outerHTML; })
      }));
    } catch (e) { /* quota exceeded — caching is optional */ }
  }

  /* --------------------------------------------------------- review record */

  function ratingOf(rev) {
    var r = rev.querySelector('.jdgm-rev__rating');
    var n = r ? parseFloat(r.getAttribute('data-score')) : NaN;
    if (!isNaN(n)) return n;
    var on = rev.querySelectorAll('.jdgm-rev__rating .jdgm-star.jdgm--on').length;
    return on || null;
  }

  function record(rev, idx) {
    var parts = [];
    ['.jdgm-rev__title', '.jdgm-rev__body', '.jdgm-rev__author'].forEach(function (sel) {
      var e = rev.querySelector(sel);
      if (e) parts.push(e.textContent);
    });
    return {
      node: rev,
      idx: idx,
      id: (rev.getAttribute('data-review-id') || '').toLowerCase(),
      author: (rev.querySelector('.jdgm-rev__author') || {}).textContent || '',
      text: fold(parts.join(' ')),
      rating: ratingOf(rev),
      thumbs: parseInt(rev.getAttribute('data-thumb-up-count'), 10) || 0,
      hasPic: !!rev.querySelector('.jdgm-rev__pics img, .jdgm-rev__pic-link'),
      hasVid: !!rev.querySelector('.jdgm-rev__vids, .jdgm-vid-player')
    };
  }

  function applyFilter(records, excludeIds) {
    var kept = [], dropped = [];
    records.forEach(function (r) {
      if (r.id && excludeIds[r.id]) dropped.push({ id: r.id, author: r.author, excerpt: r.text.slice(0, 80) });
      else kept.push(r);
    });
    return { kept: kept, dropped: dropped };
  }

  /* ----------------------------------------------------------------- sort */

  function sortKind(select) {
    if (!select) return 'recent';
    var opt = select.options && select.options[select.selectedIndex];
    var s = fold((select.value || '') + ' ' + (opt ? opt.textContent : ''));
    if (/highest/.test(s)) return 'highest';
    if (/lowest/.test(s)) return 'lowest';
    if (/video/.test(s)) return 'videos';
    if (/picture|photo/.test(s)) return 'pictures';
    if (/helpful/.test(s)) return 'helpful';
    return 'recent';
  }

  // The API returns default order (most recent first), so the original index IS
  // "most recent". That avoids parsing localized timestamps entirely.
  function sortRecords(list, kind) {
    var a = list.slice();
    switch (kind) {
      case 'highest':
        a.sort(function (x, y) { return (y.rating || 0) - (x.rating || 0) || x.idx - y.idx; }); break;
      case 'lowest':
        a.sort(function (x, y) { return (x.rating || 0) - (y.rating || 0) || x.idx - y.idx; }); break;
      case 'helpful':
        a.sort(function (x, y) { return y.thumbs - x.thumbs || x.idx - y.idx; }); break;
      case 'pictures':
        a.sort(function (x, y) { return (y.hasPic ? 1 : 0) - (x.hasPic ? 1 : 0) || x.idx - y.idx; }); break;
      case 'videos':
        a.sort(function (x, y) { return (y.hasVid ? 1 : 0) - (x.hasVid ? 1 : 0) || x.idx - y.idx; }); break;
      default:
        a.sort(function (x, y) { return x.idx - y.idx; });
    }
    return a;
  }

  /* ---------------------------------------------------------------- media */

  /* Judge.me code-splits picture/video handling into widget_media.js and only pulls it
     in when media happens to be in the DOM at page load (widget_load_with_code_splitting).
     Our paginated pages can contain media the originally rendered page never had, so the
     module has to be fetched on demand.

     Measured on a live PDP: loading it with a plain <script src> is what makes it assign
     jdgm.setupPictures / _setupVideoPlayers / _insertIframesToYoutubeContainers -- those
     keys already exist on jdgm as undefined, and the module fills them in. Going through
     jdgm.loadScript(jdgm.widgetPath('media.js')) does NOT work: widgetPath resolves to
     .../widget_v3/media.js, which 404s. The real file is CDN_BASE_URL + widget_media.js,
     read off jdgm at runtime so the Shopify extension hash is never hardcoded here.

     With the module in place, _setupVideoPlayers builds the real player from
     data-external-id (verified: it produced a player.vimeo.com iframe and cleared
     .jdgm--loading), so videos are handled by Judge.me's own code rather than guessed. */
  var mediaModule = null;

  function loadMediaModule() {
    if (mediaModule) return mediaModule;
    var j = window.jdgm;
    if (j && typeof j.setupPictures === 'function') {
      mediaModule = Promise.resolve();
      return mediaModule;
    }
    mediaModule = new Promise(function (resolve) {
      var base = j && j.CDN_BASE_URL;
      if (!base) { resolve(); return; }
      var s = document.createElement('script');
      s.src = base + 'widget_media.js';
      s.async = true;
      s.onload = s.onerror = function () { resolve(); };
      document.head.appendChild(s);
    });
    return mediaModule;
  }

  function initMedia(container) {
    var j = window.jdgm, $ = j && j.$;
    if ($) {
      var $revs = $(container).find('.jdgm-rev');
      try { if (typeof j.setupPictures === 'function') j.setupPictures($revs); } catch (e) {}
      try {
        if (typeof j._setupVideoPlayers === 'function') {
          j._setupVideoPlayers($(container).find('.jdgm-rev__vids > .jdgm-vid-player'));
        }
      } catch (e) {}
      try {
        if (typeof j._insertIframesToYoutubeContainers === 'function') {
          j._insertIframesToYoutubeContainers($revs);
        }
      } catch (e) {}
    }

    /* Pictures need one more step. Judge.me lazy-loads them with the jQuery `unveil`
       plugin, which promotes data-src -> src only while the image is inside the viewport
       when it binds; a page we render on a click carries no such guarantee, and the
       images stay blank with .jdgm--loading stuck on the link. Promote explicitly and
       hand the deferring to the browser's native lazy-loading, which has no such
       binding-time dependency. */
    container.querySelectorAll('.jdgm-rev__pic-img[data-src]').forEach(function (img) {
      if (!img.getAttribute('src')) {
        img.setAttribute('loading', 'lazy');
        img.setAttribute('src', img.getAttribute('data-src'));
      }
      var pending = img.closest('.jdgm--loading');
      if (pending) pending.classList.remove('jdgm--loading');
    });
  }

  function setupMedia(container) {
    if (!container.querySelector('.jdgm-rev__pic-link, .jdgm-vid-player, .jdgm-yt-video')) return;
    loadMediaModule().then(function () { initMedia(container); });
  }

  /* --------------------------------------------------------------- render */

  function buildPaginate(state, pages) {
    var host = state.container.parentNode;
    if (!host) return;
    Array.prototype.slice.call(host.querySelectorAll('.jdgm-paginate')).forEach(function (el) {
      el.parentNode.removeChild(el);
    });
    if (pages <= 1) return;

    // Judge.me's own class names are reused so the section CSS applies unchanged.
    var wrap = document.createElement('div');
    wrap.className = 'jdgm-paginate jmwrf-paginate';
    wrap.setAttribute('data-per-page', String(state.perPage));

    function link(page, cls, label, text) {
      var a = document.createElement('a');
      a.className = 'jdgm-paginate__page' + (cls ? ' ' + cls : '');
      a.setAttribute('data-page', String(page));
      a.setAttribute('role', 'button');
      a.setAttribute('tabindex', '0');
      a.setAttribute('aria-label', label);
      if (page === state.page) a.setAttribute('aria-current', 'page');
      a.textContent = text == null ? '' : text;
      wrap.appendChild(a);
    }

    var from = Math.max(1, Math.min(state.page - 2, pages - 4));
    var to = Math.min(pages, from + 4);
    if (state.page > 1) link(state.page - 1, 'jdgm-paginate__prev-page', 'Previous page', '');
    for (var p = from; p <= to; p++) {
      link(p, p === state.page ? 'jdgm-curt' : '', 'Page ' + p, String(p));
    }
    if (state.page < pages) link(state.page + 1, 'jdgm-paginate__next-page', 'Next page', '');
    if (to < pages) link(pages, 'jdgm-paginate__last-page', 'Page ' + pages, '');

    function go(a) {
      var p = parseInt(a.getAttribute('data-page'), 10);
      if (!p || p === state.page) return;
      state.page = p;
      render(state);
      if (state.container.getBoundingClientRect().top < 0) {
        state.container.scrollIntoView({ block: 'start', behavior: 'smooth' });
      }
    }

    wrap.addEventListener('click', function (e) {
      var a = e.target && e.target.closest ? e.target.closest('.jdgm-paginate__page') : null;
      if (!a) return;
      e.preventDefault();
      e.stopPropagation();
      go(a);
    });
    wrap.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      var a = e.target && e.target.closest ? e.target.closest('.jdgm-paginate__page') : null;
      if (!a) return;
      e.preventDefault();
      go(a);
    });

    host.appendChild(wrap);
  }

  function render(state) {
    var list = state.sorted;
    var pages = Math.max(1, Math.ceil(list.length / state.perPage));
    if (state.page > pages) state.page = pages;
    var start = (state.page - 1) * state.perPage;
    var slice = list.slice(start, start + state.perPage);

    state.muted = true;                       // our own writes must not retrigger the guard
    state.container.textContent = '';
    slice.forEach(function (r) {
      // importNode keeps the source node pristine so it can be reused on other pages
      // (jdgm.customizeReviews mutates whatever is actually in the document).
      var el = document.importNode(r.node, true);
      el.setAttribute('data-' + MARK, '1');
      state.container.appendChild(el);
    });
    buildPaginate(state, pages);
    state.muted = false;

    try {
      if (window.jdgm && window.jdgm.customizeReviews) window.jdgm.customizeReviews();
    } catch (e) {}

    setupMedia(state.container);
  }

  function recount(state) {
    var n = state.kept.length;
    var rated = state.kept.filter(function (r) { return r.rating != null; });
    var avg = rated.length
      ? rated.reduce(function (s, r) { return s + r.rating; }, 0) / rated.length
      : null;

    // Replace the leading number but keep whatever wording the merchant configured.
    // The group only swallows a separator when three more digits follow, so a
    // thousands-grouped "1 234 reviews" is matched whole while the space in
    // "258 verified reviews" is left alone.
    function setCount(el) {
      if (!el) return;
      var t = el.textContent;
      el.textContent = /\d/.test(t)
        ? t.replace(/\d+(?:[.,\u202f\u00a0 ]\d{3})*/, String(n))
        : (n + ' verified reviews');
    }
    setCount(state.root.querySelector('.jmw-v2-summary__count'));
    setCount(state.root.querySelector('.jdgm-rev-widg__summary-text'));

    if (avg != null) {
      var score = state.root.querySelector('.jmw-v2-summary__score');
      if (score) score.textContent = avg.toFixed(1);          // matches the Liquid `round: 1`
      var jmAvg = state.root.querySelector('.jdgm-rev-widg__summary-average');
      if (jmAvg) jmAvg.textContent = avg.toFixed(2);          // Judge.me's own 2dp style
    }
    return { count: n, average: avg == null ? null : Number(avg.toFixed(2)) };
  }

  /* ------------------------------------------------------------ per widget */

  function resolveContainer(widgetEl) {
    var box = widgetEl.querySelector('.jdgm-rev-widg__reviews');
    if (box) return box;
    var host = widgetEl.querySelector('.jdgm-rev-widg') || widgetEl;
    box = document.createElement('div');
    box.className = 'jdgm-rev-widg__reviews';
    host.appendChild(box);
    return box;
  }

  function perPageFor(cfg, widgetEl) {
    if (cfg.perPage > 0) return cfg.perPage;
    var pag = widgetEl.querySelector('.jdgm-paginate');
    var n = pag ? parseInt(pag.getAttribute('data-per-page'), 10) : NaN;
    if (n > 0) return n;
    n = window.jdgmSettings && parseInt(window.jdgmSettings.product_review_widget_per_page, 10);
    return n > 0 ? n : 5;
  }

  function run(root, cfg, widgetEl, productId, log) {
    var shop = shopDomain();
    if (!shop) return Promise.reject(new Error('no shop domain'));

    var cached = readCache(productId);
    var source = cached ? Promise.resolve(cached) : fetchAll(shop, productId, log);

    return source.then(function (data) {
      if (!data.nodes.length) throw new Error('no reviews parsed');
      if (!cached) writeCache(productId, data.total, data.nodes);

      var records = data.nodes.map(record);
      var res = applyFilter(records, cfg.excludeIds);

      var state = {
        root: root,
        widget: widgetEl,
        container: resolveContainer(widgetEl),
        perPage: perPageFor(cfg, widgetEl),
        kept: res.kept,
        page: 1,
        muted: false
      };

      var select = root.querySelector('.jdgm-sort-dropdown');
      state.sorted = sortRecords(res.kept, sortKind(select));
      if (select) {
        select.addEventListener('change', function (e) {
          e.stopPropagation();
          state.sorted = sortRecords(state.kept, sortKind(select));
          state.page = 1;
          render(state);
        });
      }

      render(state);
      var totals = cfg.recount ? recount(state) : null;

      // Judge.me can re-render the list later (locale reload, its own late AJAX).
      // If anything we did not inject shows up, take the container back.
      new MutationObserver(function () {
        if (state.muted) return;
        if (state.container.querySelector('.jdgm-rev:not([data-' + MARK + '])')) render(state);
      }).observe(state.container, { childList: true });

      if (cfg.debug) {
        console.groupCollapsed('[jmw-review-filter] product ' + productId +
          ': kept ' + res.kept.length + ' of ' + records.length +
          ' (judge.me total ' + data.total + ')' + (cached ? ' [from cache]' : ''));
        console.log('per page:', state.perPage,
          '| pages:', Math.ceil(res.kept.length / state.perPage),
          '| recount:', totals);
        if (res.dropped.length) console.table(res.dropped);
        else console.log('nothing dropped — none of the listed IDs matched');
        console.groupEnd();
      }
    });
  }

  /* ------------------------------------------------------------- bootstrap */

  function widgetsIn(root) {
    var list = [];
    root.querySelectorAll('.jdgm-widget[data-id], .jmw-v2-multi__item[data-id]').forEach(function (el) {
      var id = el.getAttribute('data-id');
      if (id) list.push({ el: el, productId: id });
    });
    if (!list.length) {
      var pid = root.getAttribute('data-product-id');
      var w = root.querySelector('.jdgm-rev-widg') || root.querySelector('.jdgm-review-widget');
      if (w && pid) list.push({ el: w, productId: pid });
    }
    return list;
  }

  function start(cfgEl) {
    var cfg;
    try { cfg = JSON.parse(cfgEl.textContent); } catch (e) { return; }
    var root = document.getElementById('jmw-' + cfg.sectionId);
    if (!root) return;

    function log(msg) { console.warn('[jmw-review-filter] ' + msg); }
    function reveal() { root.classList.add(READY_CLASS); }

    cfg.excludeIds = parseIds(cfg.excludeIds);
    cfg.perPage = parseInt(cfg.perPage, 10) || 0;

    // Nothing to exclude: leave Judge.me completely alone.
    if (!Object.keys(cfg.excludeIds).length) { reveal(); return; }

    var began = false;
    function begin() {
      if (began) return;
      began = true;
      // The widget element can arrive late on the app-block path.
      waitFor(function () {
        var w = widgetsIn(root);
        return w.length ? w : null;
      }, 10000).then(function (widgets) {
        if (!widgets) { log('no Judge.me widget found in this section'); return null; }
        return Promise.all(widgets.map(function (w) {
          return run(root, cfg, w.el, w.productId, log).catch(function (err) {
            log('product ' + w.productId + ': ' + ((err && err.message) || err));
          });
        }));
      }).catch(function (err) {
        log((err && err.message) || 'unexpected failure');
      }).then(reveal);   // always reveal, filtered or not
    }

    // Below the fold on a landing page — no reason to spend requests before it is near.
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        if (entries.some(function (e) { return e.isIntersecting; })) { io.disconnect(); begin(); }
      }, { rootMargin: '600px 0px' });
      io.observe(root);
      setTimeout(begin, 8000);   // safety net if the section never intersects
    } else {
      begin();
    }
  }

  function boot() {
    document.querySelectorAll('script[type="application/json"][id^="jmwrf-cfg-"]').forEach(start);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
